import React, { useEffect, useState } from "react";
import "react-modern-calendar-datepicker/lib/DatePicker.css";
import DatePicker from "react-modern-calendar-datepicker";
import {
  ymdToDate,
  dateToYmd,
  formatDate,
  calendarLocale,
  isValidDate,
} from "../Utilities/Utilities";
import { Calendar } from "react-feather";
import classNames from "classnames";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subDays,
  startOfYear,
  endOfYear,
  subMonths,
} from "date-fns";

/*
  Example for a date range picker, with a min and max with all the buttons:

  <DateSelectSpan
    label="Select a date range"
    containerClass="w-full"
    min={new Date(2022, 0, 1)}
    max={new Date(2023, 11, 31)}
    value={{from: values.start_date, to: null}}
    buttons={['clear', 'week', 'month', 'year', 'lastWeek', 'lastMonth', 'lastYear']}
    onChange={(value) => { console.log('New value from span picker', value); }}
  />

*/
export function DateSelectSpan({
  label = null,
  containerClass = "",
  labelClass = "",
  className = "",
  value = null,
  min = null,
  max = null,
  buttons = [], // buttons to enable to select different spans, options are: 'clear', 'week', 'month', 'year', 'lastWeek', 'lastMonth', 'lastYear'
  onChange = () => {},
  ...props
}) {
  const [selectedDay, setSelectedDay] = useState({
    from: value?.from
      ? dateToYmd(
          typeof value.from === "string" ? new Date(value.from) : value.from,
        )
      : null,
    to: value?.to
      ? dateToYmd(typeof value.to === "string" ? new Date(value.to) : value.to)
      : null,
  });
  const [selectedDate, setSelectedDate] = useState({
    from: value?.from
      ? typeof value.from === "string"
        ? new Date(value.from)
        : value.from
      : null,
    to: value?.to
      ? typeof value.to === "string"
        ? new Date(value.to)
        : value.to
      : null,
  });
  const [minimumDate, setMinimumDate] = useState(dateToYmd(min) || null);
  const [maximumDate, setMaximumDate] = useState(dateToYmd(max) || null);

  const whenChanged = (value) => {
    const date = {
      from: value.from ? ymdToDate(value.from) : null,
      to: value.to ? ymdToDate(value.to) : null,
    };
    setSelectedDay(value);
    setSelectedDate(date);
    onChange(date);
  };

  const formatInputValue = () => {
    if (!selectedDate?.from || !selectedDate?.to) return "Pick a span";

    // incase we get YYY-mm-dd format as inputs
    if (typeof selectedDate?.from === "string") {
      selectedDate.from = new Date(value.from);
    }
    if (typeof selectedDate?.to === "string") {
      selectedDate.to = new Date(value.to);
    }

    // console.log('formatInputValue', typeof selectedDate.from, typeof selectedDate.to, 'selectedDate.from', (selectedDate.from), 'selectedDate.to', (selectedDate.to));
    if (!isValidDate(selectedDate.from) || !isValidDate(selectedDate.to))
      return "Pick a span";

    return (
      formatDate(selectedDate?.from?.toISOString()) +
      " - " +
      formatDate(selectedDate?.to?.toISOString())
    );
  };

  const renderCustomInput = ({ ref }) => (
    <div
      className={classNames(
        className,
        "relative z-0 flex min-h-[46px] w-[18rem] w-full cursor-pointer border border-borderColor bg-white text-gray-700",
      )}
    >
      <div
        className={classNames(
          "pointer-events-none absolute bottom-0 left-4 right-4 top-0 flex max-w-full items-center justify-start text-gray-700",
        )}
      >
        <Calendar className="mr-2" />
        <span className="mt-1">{formatInputValue()}</span>
      </div>
      <input
        readOnly
        ref={ref}
        className="h-full min-h-[46px] w-full appearance-none border-none bg-transparent py-2 pl-10 pr-3 text-gray-700  focus:border-cyan-600 focus:ring focus:ring-cyan-600 focus:ring-opacity-50"
        {...props}
      />
    </div>
  );

  const renderCustomFooter = () => (
    <div className="flex flex-row flex-wrap items-center justify-center">
      {buttons.includes("clear") && (
        <div className="relative mb-3 border-4 border-transparent p-4">
          <button
            className="relative flex h-10 w-16 items-center justify-center rounded-md border border-gray-200 text-base text-gray-700 shadow-sm hover:bg-gray-100"
            type="button"
            onClick={() => {
              setSelectedDay({ from: null, to: null });
              whenChanged({ from: null, to: null });
            }}
          >
            Clear
          </button>
        </div>
      )}
      {buttons.includes("week") && (
        <div className="relative border-4 border-transparent p-4">
          <button
            className="relative flex h-10 w-24 items-center justify-center rounded-md border border-gray-200 text-base text-gray-700 shadow-sm hover:bg-gray-100"
            type="button"
            onClick={() => {
              const today = new Date();
              let from = startOfWeek(today, { weekStartsOn: 1 });
              let to = endOfWeek(today, { weekStartsOn: 1 });
              setSelectedDay({ from: dateToYmd(from), to: dateToYmd(to) });
              whenChanged({ from: dateToYmd(from), to: dateToYmd(to) });
            }}
          >
            This week
          </button>
        </div>
      )}
      {buttons.includes("month") && (
        <div className="relative border-4 border-transparent p-4">
          <button
            className="relative flex h-10 w-24 items-center justify-center rounded-md border border-gray-200 text-base text-gray-700 shadow-sm hover:bg-gray-100"
            type="button"
            onClick={() => {
              const today = new Date();
              let from = startOfMonth(today);
              let to = endOfMonth(today);
              setSelectedDay({ from: dateToYmd(from), to: dateToYmd(to) });
              whenChanged({ from: dateToYmd(from), to: dateToYmd(to) });
            }}
          >
            This month
          </button>
        </div>
      )}
      {buttons.includes("year") && (
        <div className="relative border-4 border-transparent p-4">
          <button
            className="relative flex h-10 w-24 items-center justify-center rounded-md border border-gray-200 text-base text-gray-700 shadow-sm hover:bg-gray-100"
            type="button"
            onClick={() => {
              const today = new Date();
              let from = startOfYear(today);
              let to = endOfYear(today);
              setSelectedDay({ from: dateToYmd(from), to: dateToYmd(to) });
              whenChanged({ from: dateToYmd(from), to: dateToYmd(to) });
            }}
          >
            This year
          </button>
        </div>
      )}
      {buttons.includes("lastWeek") && (
        <div className="relative border-4 border-transparent p-4">
          <button
            className="relative flex h-10 w-24 items-center justify-center rounded-md border border-gray-200 text-base text-gray-700 shadow-sm hover:bg-gray-100"
            type="button"
            onClick={() => {
              const today = subDays(
                startOfWeek(new Date(), { weekStartsOn: 1 }),
                1,
              );
              let from = startOfWeek(today, { weekStartsOn: 1 });
              let to = endOfWeek(today, { weekStartsOn: 1 });
              setSelectedDay({ from: dateToYmd(from), to: dateToYmd(to) });
              whenChanged({ from: dateToYmd(from), to: dateToYmd(to) });
            }}
          >
            Last week
          </button>
        </div>
      )}
      {buttons.includes("lastMonth") && (
        <div className="relative border-4 border-transparent p-4">
          <button
            className="relative flex h-10 w-24 items-center justify-center rounded-md border border-gray-200 text-base text-gray-700 shadow-sm hover:bg-gray-100"
            type="button"
            onClick={() => {
              const today = subDays(startOfMonth(new Date()), 1);
              let from = startOfMonth(today);
              let to = endOfMonth(today);
              setSelectedDay({ from: dateToYmd(from), to: dateToYmd(to) });
              whenChanged({ from: dateToYmd(from), to: dateToYmd(to) });
            }}
          >
            Last month
          </button>
        </div>
      )}
      {buttons.includes("lastYear") && (
        <div className="relative border-4 border-transparent p-4">
          <button
            className="relative flex h-10 w-24 items-center justify-center rounded-md border border-gray-200 text-base text-gray-700 shadow-sm hover:bg-gray-100"
            type="button"
            onClick={() => {
              const today = subDays(startOfYear(new Date()), 1);
              let from = startOfYear(today);
              let to = endOfYear(today);
              setSelectedDay({ from: dateToYmd(from), to: dateToYmd(to) });
              whenChanged({ from: dateToYmd(from), to: dateToYmd(to) });
            }}
          >
            Last year
          </button>
        </div>
      )}
      {buttons.includes("last12months") && (
        <div className="relative border-4 border-transparent p-4">
          <button
            className="relative flex h-10 w-32 items-center justify-center rounded-md border border-gray-200 text-base text-gray-700 shadow-sm hover:bg-gray-100"
            type="button"
            onClick={() => {
              const today = new Date();
              let from = startOfMonth(subMonths(today, 12));
              let to = endOfMonth(subMonths(today, 1));

              setSelectedDay({ from: dateToYmd(from), to: dateToYmd(to) });
              whenChanged({ from: dateToYmd(from), to: dateToYmd(to) });
            }}
          >
            Last 12 months
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className={`flex flex-col ${containerClass}`}>
      {label && (
        <label className={labelClass}>
          <div className="flex flex-row justify-between">
            <span className={`mb-1 block text-[14px]`}>{label}</span>
          </div>
        </label>
      )}
      <DatePicker
        className="w-full"
        value={selectedDay}
        onChange={whenChanged}
        locale={calendarLocale}
        inputPlaceholder="Select a date" // placeholder
        formatInputText={formatInputValue} // format value
        inputClassName="my-custom-input" // custom class
        minimumDate={minimumDate}
        maximumDate={maximumDate}
        renderInput={renderCustomInput} // render a custom input
        colorPrimary="#1F4444"
        colorPrimaryLight="#AACCCC"
        renderFooter={renderCustomFooter}
        calendarPopperPosition="bottom"
        shouldHighlightWeekends
      />
    </div>
  );
}
